import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CreateTeamMemberPayload,
  TeamMember,
  TeamMemberLevel,
  TeamMemberType,
  TeamSearchResults,
  UpdateTeamMemberPayload,
} from '../models/team-member';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import {
  selectAllTeams,
  selectDeleteTeamMemberError,
  selectError,
  selectLoading,
  selectLoadingRoles,
  selectRoles,
  selectRolesError,
  selectTeamMemberLoadingOrError,
  selectTeams,
} from '../redux/team-management.selectors';
import environment from '@environments/environment';
import {
  clearDeleteTeamMemberError,
  loadingRoles,
  loadingTeam,
  savedTeamMember,
  storeDeleteTeamMemberError,
  storeRoles,
  storeRolesError,
  storeSavingTeamMember,
  storeSavingTeamMemberError,
  storeTeam,
  storeTeamError,
} from '../redux/team-management.actions';
import { Role, RoleResponse } from '@team-management/models/roles';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '@shared/components/custom-snack-bar/custom-snackbar.component';
import { WcgMenuOption } from '@wcg/frontend-framework';

@Injectable()
export class TeamManagementService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _store = inject(Store);
  private readonly _snackBar = inject(MatSnackBar);

  /**
   * Get roles for a given level
   */
  public getRoles(level: TeamMemberLevel): Observable<Role[]> {
    return this._store.select(selectLoadingRoles(level)).pipe(
      take(1),
      mergeMap((loading) => {
        if (!loading) {
          this.loadRoles(level);
        }
        return this._store.select(selectRoles(level));
      }),
    );
  }

  /**
   * Get an observable of the error state for given role level
   */
  public getRolesError(level: TeamMemberLevel): Observable<string | null> {
    return this._store.select(selectRolesError(level));
  }

  /**
   * Get an observable of the loading state for given role level
   */
  public getLoadingRoles(level: TeamMemberLevel): Observable<boolean> {
    return this._store.select(selectLoadingRoles(level));
  }

  /**
   * Get an observable of the error state for given study's team member type and level
   */
  public getLoadTeamError(
    entityId: string,
    memberType: TeamMemberType,
    level: TeamMemberLevel,
  ): Observable<string | null> {
    return this._store
      .select(selectError(entityId, level))
      .pipe(map((error) => error?.[memberType]));
  }

  /**
   * Get an observable of the error state for deleting a team member
   */
  public getDeleteTeamMemberError(memberId: string): Observable<string | null> {
    return this._store.select(selectDeleteTeamMemberError(memberId));
  }

  /**
   * Get an observable of the loading state for given study's team member type and level
   */
  public getLoadingTeamMembers(
    entityId: string,
    memberType: TeamMemberType,
    level: TeamMemberLevel,
  ): Observable<boolean> {
    return this._store
      .select(selectLoading(entityId, level))
      .pipe(map((loading) => loading?.[memberType]));
  }

  /**
   * Load the team members for a given study
   */
  public loadTeamMembers(
    entityId: string,
    memberType: TeamMemberType,
    pageSize: number,
    page: number,
    level: TeamMemberLevel,
    searchValue?: string,
    siteId?: string,
    filters?: any,
    sort_by_field?: string,
    sort_by_order?: string,
  ): Observable<TeamSearchResults> {
    return this._store.select(selectLoading(entityId, level)).pipe(
      map((loading) => loading?.[memberType]),
      take(1),
      tap((loading) => {
        if (!loading) {
          this.loadTeam(
            entityId,
            memberType,
            pageSize,
            page,
            level,
            searchValue,
            siteId,
            filters,
            sort_by_field,
            sort_by_order,
          );
        }
      }),
      switchMap(() =>
        this._store
          .select(selectTeams(entityId, level))
          .pipe(map((entry) => entry[memberType])),
      ),
    );
  }

  public getTotalTeamMembers(
    entityId: string,
    level: TeamMemberLevel,
    otherData?: any,
  ): Observable<{
    client: number;
    wcg: number;
  }> {
    return this._store.select(selectAllTeams(entityId, level)).pipe(
      take(1),
      mergeMap((entry) => {
        const hasClient = !!entry.client;
        const hasWcg = !!entry.wcg;

        if (hasClient && hasWcg) {
          return of(entry);
        }
        const subs: Observable<boolean>[] = [];

        if (hasClient) {
          this.loadTeam(
            entityId,
            'client',
            10,
            1,
            level,
            '',
            otherData?.siteId,
          );
          subs.push(
            this.getLoadingTeamMembers(entityId, 'client', level).pipe(
              filter((loading) => !loading),
              take(1),
            ),
          );
        }

        if (hasWcg) {
          this.loadTeam(entityId, 'wcg', 10, 1, level, '', otherData?.site_id);
          subs.push(
            this.getLoadingTeamMembers(entityId, 'wcg', level).pipe(
              filter((loading) => !loading),
              take(1),
            ),
          );
        }

        // Wait until all teams are loaded
        return forkJoin(subs).pipe(
          take(1),
          mergeMap(() =>
            this._store.select(selectAllTeams(entityId, level)).pipe(take(1)),
          ),
        );
      }),
      map((entry) => {
        return {
          client: entry.client?.total ?? 0,
          wcg: entry.wcg?.total ?? 0,
        };
      }),
    );
  }

  /**
   * Update a team member
   */
  public updateTeamMember(
    entityId: string,
    payload: UpdateTeamMemberPayload,
    level: TeamMemberLevel,
    siteId?: string,
  ) {
    this._store
      .select(selectTeams(entityId, level))
      .pipe(take(1))
      .subscribe((entry) => {
        this._httpClient
          .put(
            `${environment.irisApi}/study/contact/${payload.teamMember.id}/`,
            payload,
          )
          .subscribe(() => {
            this.loadTeam(
              entityId,
              payload.teamMember.team,
              entry[payload.teamMember.team]?.pageSize ?? 10,
              1,
              level,
              '',
              siteId,
            );
          });
      });
  }

  /**
   * Create a team member
   */
  public createTeamMember(
    entityId: string,
    teamMember: CreateTeamMemberPayload,
    level: TeamMemberLevel,
    siteId?: string,
  ) {
    this._store.dispatch(storeSavingTeamMember());

    this._store
      .select(selectTeams(entityId, level))
      .pipe(take(1))
      .subscribe((entry) => {
        this._httpClient
          .post(`${environment.irisApi}/study/contact/`, teamMember)
          .pipe(
            catchError((e) => {
              this._store.dispatch(
                storeSavingTeamMemberError({
                  error: e as string,
                }),
              );

              return EMPTY;
            }),
          )
          .subscribe((response: { warning?: string }) => {
            this._store.dispatch(savedTeamMember());
            if (response?.warning) {
              this._snackBar.open(response.warning, '✕', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['snack-bar-warning'],
              });
            }
            this.loadTeam(
              entityId,
              teamMember.team,
              entry[teamMember.team]?.pageSize ?? 10,
              1,
              level,
              '',
              siteId,
            );
          });
      });

    return this._store.select(selectTeamMemberLoadingOrError);
  }

  /**
   * Delete a team member
   */
  public deleteTeamMember(
    entityId: string,
    teamMember: TeamMember,
    level: TeamMemberLevel,
    siteId?: string,
  ): Observable<unknown> {
    this._store.dispatch(
      clearDeleteTeamMemberError({ memberId: teamMember.id }),
    );

    return this._store.select(selectTeams(entityId, level)).pipe(
      take(1),
      switchMap((entry) =>
        this._httpClient
          .delete(`${environment.irisApi}/study/contact/${teamMember.id}/`)
          .pipe(
            tap(() => {
              this.loadTeam(
                entityId,
                teamMember.team,
                entry[teamMember.team]?.pageSize ?? 10,
                1,
                level,
                '',
                siteId,
              );
            }),
            catchError((e) => {
              console.error(e);

              this._store.dispatch(
                storeDeleteTeamMemberError({
                  memberId: teamMember.id,
                  error: 'Failed to delete team member',
                }),
              );

              return throwError(() => e);
            }),
          ),
      ),
    );
  }

  public resendInvite(
    entityId: string,
    teamMember: TeamMember,
    level: TeamMemberLevel,
    siteId?: string,
  ) {
    this._store
      .select(selectTeams(entityId, level))
      .pipe(take(1))
      .subscribe((entry) => {
        this._httpClient
          .post(
            `${environment.irisApi}/resend-invite/${teamMember.invitation.id}/`,
            {},
          )
          .subscribe(() => {
            this._snackBar.openFromComponent(SnackbarComponent, {
              duration: 1000,
              data: 'Invite sent',
              horizontalPosition: 'start',
              verticalPosition: 'bottom',
            });
            this.loadTeam(
              entityId,
              teamMember.team,
              entry[teamMember.team]?.pageSize ?? 10,
              1,
              level,
              '',
              siteId,
            );
          });
      });
  }

  /**
   * Load roles and store them in the state
   */
  private loadTeam(
    entityId: string,
    memberType: TeamMemberType,
    pageSize: number,
    page: number,
    level: TeamMemberLevel,
    searchValue?: string,
    siteId?: string,
    filters?: any,
    sort_by_field?: string,
    sort_by_order?: string,
  ) {
    this._store.dispatch(
      loadingTeam({
        entityId,
        memberType,
        level,
      }),
    );

    const params: any = {
      query: searchValue?.replace(/[&/\\#,+()$~%'":*?<>{}]/g, '') ?? '',
      level: level,
      team: memberType,
      page_size: pageSize.toString(),
      page: page.toString(),
      site_id: siteId ?? '',
    };
    // if (level === 'site') {
    //   params.site_id = entityId;
    // }
    if (level === 'study') {
      params.study_id = entityId;
    }
    if (filters) {
      params.filters = filters;
    }
    if (typeof sort_by_field === 'string' && sort_by_field !== '{}') {
      params.sort_by_field = sort_by_field;
    }
    if (typeof sort_by_order === 'string' && sort_by_order !== '{}') {
      params.sort_by_order = sort_by_order;
    }

    this._httpClient
      .get<{
        data: TeamMember[];
        total: number;
        roles: WcgMenuOption[];
        status: WcgMenuOption[];
        visible: WcgMenuOption[];
      }>(`${environment.irisApi}/study/${entityId}/contact/`, {
        params,
      })
      .pipe(
        catchError(() => {
          this._store.dispatch(
            storeTeamError({
              entityId,
              memberType,
              error: 'Failed to load team members',
              level,
            }),
          );

          return EMPTY;
        }),
      )
      .subscribe((response) => {
        const isWcg = memberType === 'wcg';
        let teamData = response.data;
        let totalCount = response.total;
        if (isWcg && page == 1) {
          teamData = [
            {
              name: null,
              qc_function: 'SME',
              is_visible: true,
              email: null,
              title: 'SME',
            } as unknown as TeamMember,
            {
              name: null,
              qc_function: 'QC',
              is_visible: true,
              email: null,
              title: 'QC',
            } as unknown as TeamMember,
          ];
          totalCount = 0;
        }
        this._store.dispatch(
          storeTeam({
            entityId,
            team: teamData,
            memberType,
            page,
            pageSize,
            total: totalCount,
            level,
            roles: response.roles ?? [],
            status: response.status ?? [],
            visible: response.visible ?? [],
          }),
        );
      });
  }

  /**
   * Load roles and store them in the state
   */
  private loadRoles(level: TeamMemberLevel): Observable<Role[]> {
    this._store.dispatch(loadingRoles({ level }));
    this._httpClient
      .get<RoleResponse>(`${environment.irisApi}/auth/roles/`, {
        params: {
          level_type: level,
        },
      })
      .pipe(
        catchError(() => {
          this._store.dispatch(
            storeRolesError({
              level,
              error: 'Failed to load roles',
            }),
          );

          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this._store.dispatch(storeRoles({ level, roles: response.data }));
      });

    return this._store.select(selectRoles(level));
  }

  public updateSMEQC(
    entityId: string,
    data: { user_id: string; attribute: string },
  ) {
    return this._httpClient.post(
      `${environment.irisApi}/workspace/${entityId}/user-attribute/`,
      data,
    );
  }

  public updateSMEQCNEW(
    entityId: string,
    data: { user_id: string; attribute: string },
  ) {
    return this._httpClient.post(
      `${environment.irisApi}/v2/workspace/${entityId}/user-attribute/`,
      data,
    );
  }

  public entityAccessRequest(data: {
    entityId: string | undefined;
    contactId: string;
    entityType: string;
    status: 'active' | 'denied';
    role?: string;
  }) {
    return this._httpClient.put(
      `${environment.irisApi}/${data.entityType}/${data.entityId}/access/${data.contactId}/`,
      {
        status: data.status,
        role_id: data.role,
      },
    );
  }

  public removeSMEQC(entityId: string, user_id: string, attribute: string) {
    return this._httpClient.delete(
      `${environment.irisApi}/workspace/${entityId}/user-attribute/${user_id}/${attribute}/`,
      {},
    );
  }

  public removeSMEQCNEW(entityId: string, userId: string, attribute: string) {
    return this._httpClient.delete(
      `${environment.irisApi}/v2/workspace/${entityId}/user-attribute/${userId}/${attribute}/`,
      {},
    );
  }

  getSubmissionTeam(submissionId: string): Observable<TeamMember[]> {
    return this._httpClient
      .get<{
        data: TeamMember[];
      }>(`${environment.irisApi}/workspace/${submissionId}/contact/`, {
        params: { page_size: 1000 },
      })
      .pipe(map((response) => response.data));
  }
}
